import type { LocationSearchResultDto } from '@/lib/api';
import { isUsableOfficeHour } from '@/lib/location/officeHours';

type OfficeHour = NonNullable<LocationSearchResultDto['officeHours']>[number];

/**
 * The brands all sit in one offset (Europe/Berlin == Europe/Zurich), so a fixed
 * zone is enough — and it is required: the ECS task's clock is UTC, which would
 * flip "today" an hour or two before local midnight, every evening. What stays
 * is a ≤5-minute window right after midnight in which the edge still serves the
 * previous day's line (the page's `s-maxage`), which is accepted.
 */
const BRAND_TIME_ZONE = 'Europe/Berlin';

/** `MONDAY`…`SUNDAY`, the spelling `OfficeHourDTO.weekday` carries. */
export function currentWeekday(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: BRAND_TIME_ZONE,
    weekday: 'long',
  })
    .format(now)
    .toUpperCase();
}

/**
 * Today's recurring opening hours, or `undefined` when the Standort has no
 * usable entry for today — the card then shows no opening-hours line rather
 * than an empty label. The `standard`/`disabled` filter is the shared
 * `isUsableOfficeHour()` the School Details table uses, so the two pages can
 * never disagree about which rows count; only the day narrowing is local.
 *
 * `startTime` and `endTime` are both nullable, so an entry that claims to be
 * open without saying when is dropped here too: it would otherwise render as
 * "Öffnungszeiten: heute  –  Uhr". A `closed` entry legitimately carries no
 * times — it renders its own message and keeps its line.
 */
export function todaysOfficeHour(
  officeHours: OfficeHour[] | undefined,
  weekday: string = currentWeekday(),
): OfficeHour | undefined {
  return officeHours?.find(
    (entry) =>
      isUsableOfficeHour(entry) &&
      entry.weekday === weekday &&
      (entry.closed === true || (!!entry.startTime && !!entry.endTime)),
  );
}
